import { useRef, useEffect, useState, Suspense } from 'react';
import { useFrame, Canvas } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

function HumanBodyModel() {
  const { scene } = useGLTF('/human-body.glb');
  const modelRef = useRef<THREE.Group>(null);
  const [targetRotation, setTargetRotation] = useState({ x: 0, y: 0 });
  const currentRotationRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const { clientX } = event;
      const { innerWidth } = window;
      
      // Normalize mouse position to -1 to 1 range (horizontal only)
      const x = (clientX / innerWidth) * 2 - 1;
      
      // Calculate target rotation (45 degrees = 0.785 radians) - horizontal only
      const maxRotation = Math.PI / 4; // 45 degrees
      const targetY = x * maxRotation;
      const targetX = 0; // No vertical rotation
      
      setTargetRotation({ x: targetX, y: targetY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Smooth interpolation to target rotation using refs (no re-render)
  useFrame((state) => {
    if (modelRef.current) {
      // Lerp towards target rotation
      const lerpFactor = 0.05;
      const cr = currentRotationRef.current;
      cr.x += (targetRotation.x - cr.x) * lerpFactor;
      cr.y += (targetRotation.y - cr.y) * lerpFactor;
      
      // Apply rotation with gentle idle animation (horizontal only)
      const idleY = Math.sin(state.clock.elapsedTime * 0.3) * 0.05;
      
      modelRef.current.rotation.y = cr.y + idleY;
      modelRef.current.rotation.x = 0; // No vertical rotation
    }
  });

  return (
    <group ref={modelRef}>
      <primitive 
        object={scene} 
        scale={[3, 3, 3]} 
        position={[0, -2.5, 0]}
        rotation={[0, Math.PI * 0.1, 0]}
      />
    </group>
  );
}

function HumanBody3DCanvas() {
  return (
    <Canvas
      camera={{ 
        position: [0, 0, 8], 
        fov: 50 
      }}
      style={{ 
        width: '100%', 
        height: '100%',
        background: 'transparent'
      }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight 
        position={[10, 10, 5]} 
        intensity={1}
        castShadow
      />
      <pointLight 
        position={[-10, -10, -5]} 
        intensity={0.5}
        color="#2BB6A3"
      />
      <Suspense fallback={null}>
        <HumanBodyModel />
      </Suspense>
    </Canvas>
  );
}

export default function HumanBody3D() {
  return (
    <div className="human-body-3d">
      <Suspense fallback={<div className="component-loader">Loading 3D model...</div>}>
        <HumanBody3DCanvas />
      </Suspense>
    </div>
  );
}

// Preload the model
useGLTF.preload('/human-body.glb');
